/**
 * Utility functions for Calendar module
 */

import { format, parse, startOfWeek, getDay } from 'date-fns';
import { dateFnsLocalizer } from 'react-big-calendar';
import { CalendarEvent } from '../../lib/api';
import { STATUS_COLORS, EVENT_TYPE_BG_COLORS } from './constants';
import { EventsByMonth } from './types';

const locales = {
  'en-US': require('date-fns/locale/en-US'),
};

export const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

export function formatDateTime(datetime: string): string {
  const date = new Date(datetime);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function formatDate(datetime: string): string {
  const date = new Date(datetime);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export function getStatusBadgeClass(status: string): string {
  return STATUS_COLORS[status] || 'bg-gray-100 text-gray-800';
}

export function getEventStyleGetter(event: { resource?: unknown }) {
  const resource = event.resource as { event_type?: string } | undefined;
  const backgroundColor = EVENT_TYPE_BG_COLORS[resource?.event_type || ''] || '#6b7280';
  return {
    style: {
      backgroundColor,
      borderRadius: '4px',
      opacity: 0.8,
      color: 'white',
      border: '0px',
      display: 'block',
    },
  };
}

export function groupEventsByMonth(events: CalendarEvent[]): EventsByMonth {
  const groups: EventsByMonth = {};

  events.forEach((event) => {
    const monthKey = format(new Date(event.start_datetime), 'MMMM yyyy');
    if (!groups[monthKey]) {
      groups[monthKey] = [];
    }
    groups[monthKey].push(event);
  });

  // Sort events within each month by start date
  Object.keys(groups).forEach((key) => {
    groups[key].sort(
      (a, b) => new Date(a.start_datetime).getTime() - new Date(b.start_datetime).getTime()
    );
  });

  return groups;
}

export function filterEvents(
  events: CalendarEvent[],
  activeEventTypes: string[],
  selectedSupplier: string,
  eventPeriod: string
): CalendarEvent[] {
  return events.filter((event) => {
    const typeMatch = activeEventTypes.includes(event.event_type);
    const supplierMatch =
      selectedSupplier === 'all' || event.company === parseInt(selectedSupplier);

    // Period filtering
    const now = new Date();
    const eventStart = new Date(event.start_datetime);
    const threeMonthsAgo = new Date(now);
    threeMonthsAgo.setMonth(now.getMonth() - 3);
    const sixMonthsAgo = new Date(now);
    sixMonthsAgo.setMonth(now.getMonth() - 6);

    let periodMatch = true;
    if (eventPeriod === 'upcoming') {
      // R-283: an OVERDUE obligation is still outstanding work, so it belongs
      // in "upcoming" — it is the first thing the viewer needs, not the last.
      // Before this, /calendar's default period dropped every past-due row, so
      // the deadlines a supplier was LATE on were the one population the
      // default view could not show. `metadata.is_overdue` is set by the
      // backend on all four synthetic deadline types (NCR containment, NCR
      // CAR, QBR, action due) and on nothing else, so real CalendarEvent rows
      // keep the plain end-date rule.
      const stillOwed = (event as { metadata?: { is_overdue?: boolean } })
        .metadata?.is_overdue === true;
      periodMatch = stillOwed || new Date(event.end_datetime) >= now;
    } else if (eventPeriod === 'last_3_months') {
      periodMatch = eventStart >= threeMonthsAgo;
    } else if (eventPeriod === 'last_6_months') {
      periodMatch = eventStart >= sixMonthsAgo;
    }

    return typeMatch && supplierMatch && periodMatch;
  });
}

export function transformEventsForCalendar(events: CalendarEvent[]) {
  return events.map((event) => ({
    id: event.id,
    title: event.title,
    start: new Date(event.start_datetime),
    end: new Date(event.end_datetime),
    resource: event,
    allDay: event.all_day,
  }));
}
